import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, ArrowLeft, Building2, Menu, X, User, ChevronDown, LogOut, UserCircle, AlertTriangle } from 'lucide-react'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'

const EmployerSignupPage: React.FC = () => {
  const navigate = useNavigate()
  const { register, loading, user, logout } = useAuthStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin'
    if (user?.role === 'alumni') return '/dashboard'
    if (user?.role === 'employer') return '/employer-dashboard'
    return '/dashboard'
  }

  const handleLogout = () => {
    setIsDropdownOpen(false)
    logout()
  }

  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companySize: '',
    website: '',
  })
  const [phoneValue, setPhoneValue] = useState<string | undefined>(undefined)

  // Industry partner fields
  const [isIndustryPartner, setIsIndustryPartner] = useState(false)
  const [partnerDepartments, setPartnerDepartments] = useState<string[]>([])
  const [departments, setDepartments] = useState<Array<{ id: string; name: string; abbreviation: string }>>([])

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [duplicateEmail, setDuplicateEmail] = useState<'confirmed' | 'unconfirmed' | false>(false)
  // successEmail is set after registration — triggers the success screen
  const [successEmail, setSuccessEmail] = useState('')
  const [submittedAsPartner, setSubmittedAsPartner] = useState(false)

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('departments')
          .select('id, name, abbreviation')
          .eq('is_active', true)
          .order('display_order', { ascending: true })
        if (!error && data) setDepartments(data)
      } catch (err) {
        console.error('Failed to load departments:', err)
      }
    }
    fetchDepartments()
  }, [])

  const togglePartnerDept = (name: string) => {
    setPartnerDepartments(prev =>
      prev.includes(name) ? prev.filter(d => d !== name) : [...prev, name]
    )
    if (errors.partnerDepartments) setErrors(prev => ({ ...prev, partnerDepartments: undefined }))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required'
    if (!formData.industry.trim()) newErrors.industry = 'Industry is required'
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    if (!phoneValue) {
      newErrors.phone = 'Phone number is required'
    } else if (!isValidPhoneNumber(phoneValue)) {
      newErrors.phone = 'Enter a valid phone number for the selected country'
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match'
    if (!formData.companySize.trim()) newErrors.companySize = 'Company size is required'
    if (isIndustryPartner && partnerDepartments.length === 0) {
      newErrors.partnerDepartments = 'Select at least one partner department'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }))
    if (name === 'email' && duplicateEmail) setDuplicateEmail(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    try {
      await register(formData.email, formData.password, {
        company_name: formData.companyName,
        industry: formData.industry,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: phoneValue || '',
        company_size: formData.companySize,
        website: formData.website,
        is_industry_partner: isIndustryPartner,
        partner_departments: isIndustryPartner ? partnerDepartments : [],
        role: 'employer',
      })
      setSubmittedAsPartner(isIndustryPartner)
      setSuccessEmail(formData.email)
    } catch (error: any) {
      if (error.message === 'EMAIL_ALREADY_REGISTERED_CONFIRMED') {
        setDuplicateEmail('confirmed')
      } else if (error.message === 'EMAIL_ALREADY_REGISTERED_UNCONFIRMED') {
        setDuplicateEmail('unconfirmed')
      } else {
        setErrors({ email: error.message || 'Registration failed' })
      }
    }
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (successEmail) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              <Link to="/" className="flex items-center">
                <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
              </Link>
              <nav className="hidden md:flex space-x-8">
                <Link to="/news" className="text-gray-600 hover:text-gray-900">News &amp; Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
              </nav>
              <div className="hidden md:flex">
                <Link to="/login/employer" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Login
                </Link>
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className={`bg-white border rounded-2xl shadow-lg p-8 ${submittedAsPartner ? 'border-yellow-200' : 'border-green-200'}`}>
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${submittedAsPartner ? 'bg-yellow-100' : 'bg-green-100'}`}>
                    <Building2 className={`w-10 h-10 ${submittedAsPartner ? 'text-yellow-600' : 'text-green-600'}`} />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {submittedAsPartner ? 'Partner Request Submitted!' : 'Company Registered!'}
                </h1>
                <p className="text-gray-600 text-sm">
                  {submittedAsPartner
                    ? 'Your industry partner request will be reviewed by the relevant department admin(s).'
                    : 'Your company account has been created successfully.'}
                </p>
              </div>
              <div className={`rounded-lg p-4 mb-6 text-sm ${
                submittedAsPartner
                  ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                  : 'bg-green-50 border border-green-200 text-green-800'
              }`}>
                <p>
                  A verification email has been sent to <strong>{successEmail}</strong>. Please verify your email before logging in.
                </p>
                {submittedAsPartner && (
                  <p className="mt-2">
                    After email verification, your account will remain <strong>pending</strong> until your partnership request is approved by the department admin.
                  </p>
                )}
                {submittedAsPartner && (
                  <p className="mt-2">
                    If your request is still pending after <strong>3-5 days</strong>, please contact the related department(s) for follow-up.
                  </p>
                )}
              </div>
              <Link
                to="/login/employer"
                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors"
              >
                Go to Employer Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>

            <div className="hidden md:flex">
              {user ? (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Dashboard
                  </button>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center space-x-2 focus:outline-none"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center hover:bg-gray-400 transition-colors overflow-hidden">
                        {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                          <img
                            src={user.profile.profile_picture}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-avatar.svg'
                            }}
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-600" />
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                          <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                        </div>
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false)
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <UserCircle className="w-4 h-4" />
                          <span>View Profile</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Login
                </Link>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/news" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>News & Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>About UCLM</Link>

                <div className="pt-4 border-t border-gray-200">
                  {user ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          navigate(getDashboardRoute())
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                      >
                        Dashboard
                      </button>
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                              <img
                                src={user.profile.profile_picture}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-avatar.svg'
                                }}
                              />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate">{user?.email}</p>
                            <p className="text-[11px] text-gray-500 capitalize">{user?.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                            setIsMobileMenuOpen(false)
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          handleLogout()
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-center mb-6">
            <Building2 className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
          </div>

          <div className="text-center mb-8">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">Trailblaze</div>
            <p className="text-sm sm:text-base text-gray-600">Register Your Company</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* ─ Industry Partner Toggle ────────────────────────────────────── */}
            <div className={`border rounded-xl p-4 transition-colors ${
              isIndustryPartner ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-white'
            }`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isIndustryPartner}
                  onChange={(e) => {
                    setIsIndustryPartner(e.target.checked)
                    if (!e.target.checked) setPartnerDepartments([])
                  }}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <div>
                  <span className="text-sm font-semibold text-gray-800">My company is a UCLM Industry Partner</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Industry partners have a formal relationship with one or more UCLM colleges.
                    Selecting this will send a partnership request to the relevant department admin for approval.
                  </p>
                </div>
              </label>

              {isIndustryPartner && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Partner Departments <span className="text-red-500">*</span>
                  </p>
                  {errors.partnerDepartments && (
                    <p className="text-sm text-red-600 mb-2">{errors.partnerDepartments}</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 border border-gray-200 rounded-lg p-3 bg-white max-h-52 overflow-y-auto">
                    {departments.map(dept => (
                      <label key={dept.id} className="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-blue-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={partnerDepartments.includes(dept.name)}
                          onChange={() => togglePartnerDept(dept.name)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700 leading-tight">
                          <span className="font-medium text-gray-500 mr-1">[{dept.abbreviation}]</span>
                          {dept.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex items-start gap-2 text-xs text-yellow-800 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>
                      Your account will be <strong>pending</strong> until the selected department admin(s) approve your partnership request.
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.companyName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter company name"
                />
                {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.industry ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select industry</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Education">Education</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Other">Other</option>
                </select>
                {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="First name"
                />
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Last name"
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="company@example.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                {duplicateEmail === 'confirmed' && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    <p className="font-medium">This email is already registered.</p>
                    <p className="mt-1">Your account exists but may be pending approval.{' '}
                      <Link to="/employer/login" className="text-blue-600 hover:underline font-medium">Sign in instead →</Link>
                    </p>
                  </div>
                )}
                {duplicateEmail === 'unconfirmed' && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <p className="font-medium">This email is already registered but not yet confirmed.</p>
                    <p className="mt-1">Check your inbox for the confirmation email, or{' '}
                      <Link to="/employer/login" className="text-blue-600 hover:underline font-medium">sign in</Link>.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className={`flex rounded-lg border overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 bg-white ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}>
                  <PhoneInput
                    international
                    defaultCountry="PH"
                    value={phoneValue}
                    onChange={(val) => {
                      setPhoneValue(val)
                      if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }))
                    }}
                    className="phone-input-custom w-full"
                  />
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
                <select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.companySize ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select company size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501+">501+ employees</option>
                </select>
                {errors.companySize && <p className="mt-1 text-sm text-red-600">{errors.companySize}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website (Optional)</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://www.company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? 'Registering Company...'
                : isIndustryPartner
                  ? 'Register & Submit Partner Request'
                  : 'Register Company'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login/employer" className="text-sm sm:text-base text-blue-600 hover:underline">
              Back to Login
            </Link>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      </div>
    </div>
  )
}

export default EmployerSignupPage
